import React, { useState, useEffect, useRef, useCallback } from 'react';
import { resolveProxyWsUrl, withWsToken } from '../utils/proxyWs';
import { useTraffic } from '../context/TrafficContext';
import { useWebUsbCapture, isActive as usbCaptureActive } from '../utils/webUsbCapture';
import { androidCaptureMix, ruleUsbBlocked } from '../utils/usbCaptureScope';
import { ppidLengthWarning, gamBodyHint } from '../utils/rewriteWarnings';
import { describeRule, validateRandomPpidDraft } from '../utils/describeRewrite';

const RANDOM_PREFIX_DEFAULT = '/4145/FNC.test';
const REJECT_REASON = {
  device_required: 'pick a device scope (a random rule never applies to all devices).',
  iuPrefix_invalid: 'the ad unit prefix needs a leading / and at least two segments, e.g. /4145/FNC.test.',
  randomValue_key_must_be_ppid: 'a random value is only supported for the ppid param.',
  randomValue_unsupported: 'that random mode is not supported.',
};
const reasonText = (r) => REJECT_REASON[r] || 'the proxy refused it.';

// Host only — each call adds its own `/proxy` prefix. See ChaosPage.jsx for
// why folding the prefix into the fallback silently 404s in prod.
const PROXY_BASE = (process.env.REACT_APP_PROXY_API_URL || '').replace(/\/$/, '');

/**
 * IuRewritePage — Ad Unit (IU) Rewrite control panel.
 *
 * Lets QA point live GAM / Prebid traffic at a TEST ad unit before the request
 * leaves the proxy, without changing the app. Rules are wildcard/prefix based:
 *   match  /4145/fnc/iosmob/hb/*   replace  /4145/FNC.test/app/banner
 * A master switch turns all rewriting on/off live (no proxy restart).
 *
 * Talks to the proxy over its own WebSocket:
 *   → get_iu_rules / toggle_iu_rewrite / set_iu_rule / delete_iu_rule / toggle_iu_rule
 *   ← iu_rules { enabled, rules[] }
 */

const C = {
  pass:   '#22c55e',
  warn:   '#f59e0b',
  fail:   '#ef4444',
  active: '#3b82f6',
  muted:  'var(--muted)',
  ink:    'var(--ink)',
  card:   'var(--card)',
  border: 'var(--border)',
  surface:'var(--surface)',
};

// Per-field metadata: which channels apply, placeholders, and the hint copy.
const FIELD_META = {
  iu: {
    name: 'Ad Unit (IU)',
    channels: ['gam', 'prebid'],
    matchPh: '/4145/fnc/iosmob/hb/*',
    replacePh: '/4145/FNC.test/app/banner',
    matchHint: 'GAM iu= · Prebid gpid / adslot. Use * to match any segment.',
    replaceHint: '* carries captured segments over.',
  },
  sz: {
    name: 'Size (sz / format)',
    channels: ['gam', 'prebid'],
    matchPh: '300x250',
    replacePh: '728x90',
    matchHint: 'Map one size to another. GAM sz= tokens · Prebid banner.format.',
    replaceHint: 'Replacement must be a clean WxH, e.g. 728x90.',
  },
  storedRequestId: {
    name: 'Stored Request ID',
    channels: ['prebid'],
    matchPh: 'banner-mobile',
    replacePh: 'banner-test',
    matchHint: 'Prebid imp.ext.prebid.storedrequest.id (+ top-level).',
    replaceHint: 'The test stored-request id to use.',
  },
  apsSlot: {
    name: 'APS Slot (UUID)',
    channels: ['aps'],
    matchPh: '11111111-2222-3333-4444-555555555555',
    replacePh: 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
    matchHint: 'Matches APS slots[].slotID exactly (wildcards allowed).',
    replaceHint: 'The test APS slot UUID.',
  },
  kvpAdd: {
    name: 'Add KVP',
    channels: ['gam', 'prebid', 'aps'],
    kvp: 'add',
    keyPh: 'qa',
    valuePh: '1',
    keyHint: 'Key to set — GAM cust_params · Prebid imp.ext.data · APS pj.cstm. Overwrites if it exists.',
    valueHint: 'Value to set for this key.',
  },
  kvpRemove: {
    name: 'Remove KVP',
    channels: ['gam', 'prebid', 'aps'],
    kvp: 'remove',
    keyPh: 'pos',
    keyHint: 'Key to remove if present — GAM cust_params · Prebid imp.ext.data · APS pj.cstm.',
  },
  paramRemove: {
    name: 'Remove query param (GAM)',
    channels: ['gam'],
    param: 'remove',
    valuePh: 'adtest',
    valueHint: 'Name of a GAM ad-request query param to delete before the request leaves the proxy (e.g. adtest). Also applies to DAI stream-create form bodies.',
  },
  paramAdd: {
    name: 'Add query param (GAM)',
    channels: ['gam'],
    param: 'add',
    keyPh: 'adtest',
    valuePh: 'on',
    keyHint: 'Name of the GAM ad-request query param to set. Overwrites it if the request already has it.',
    valueHint: 'Value to send for this param. Also applies to DAI stream-create form bodies.',
  },
  bundleSwap: {
    name: 'App bundle swap',
    channels: ['gam', 'prebid'],
    matchPh: 'com.foxnews.android.debug',
    replacePh: 'com.foxnews.android',
    matchHint: 'The bundle on the wire (e.g. a debug build). Replaced as a whole token: GAM msid, an (version prefix kept), url · Prebid app.bundle (every string in the OpenRTB body).',
    replaceHint: 'The bundle to send instead (e.g. the production app).',
  },
  metaField: {
    name: 'Set Meta ad-request field',
    channels: ['meta'],
    param: 'add',
    keyLabel: 'Form field',
    keyPh: 'IS_TEST_BUILD',
    valuePh: '0',
    keyHint: "Field in Meta Audience Network's ad request (POST network_ads_common: ep1/ep2.facebook.com on iOS, web.facebook.com on Android). Only this field changes; overwritten if present, added if not. The same key in the launch sync (adnw_sync2 on ep7.facebook.com or web.facebook.com, e.g. IDFA_FLAG) is overwritten too, but never added there.",
    valueHint: 'Value to send for this field.',
  },
  metaTestMode: {
    name: 'Meta test mode',
    channels: ['gam', 'meta'],
    fixed: true,
    note: "Turns on Meta test mode on both sides at once: adtest=on on the GAM request (GAM then sends Meta a test bid request) and TEST_MODE=1 on Meta's ad request (POST network_ads_common on ep1/ep2.facebook.com or, on Android, web.facebook.com). Meta answers 1001 no fill when only one side is in test mode. Switching the rule off removes both. Meta must already be in the auction (its bidder token in the GAM a3p), and it is unproven that Meta accepts a TEST_MODE added in transit: if Meta still answers 1001, the app has to call AdSettings test mode itself.",
  },
};

const CHANNEL_LABEL = { gam: 'GAM', prebid: 'Prebid', aps: 'APS', meta: 'Meta' };
const CHANNEL_DOT = { gam: '#3b82f6', prebid: '#a855f7', aps: '#f59e0b', meta: '#0866ff' };

const EMPTY_DRAFT = {
  id: null,
  label: '',
  field: 'iu',
  match: '',
  replace: '',
  key: '',
  value: '',
  randomValue: '',
  iuPrefix: '',
  device: '',
  targets: { gam: true, prebid: true },
  enabled: true,
};

export default function IuRewritePage() {
  const [enabled, setEnabled] = useState(false);   // master switch
  const [rules, setRules] = useState([]);
  const [wsState, setWsState] = useState('connecting');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [devices, setDevices] = useState([]); // known device ids for the scope picker
  const wsRef = useRef(null);
  // Random-ppid save in flight: { ref, draft, editingId }. Lets only the sending tab show a refusal.
  const pendingRef = useRef(null);
  const [rejectedMsg, setRejectedMsg] = useState('');
  // USB (adb logcat) capture is read-only — rules can't reach it. Used to
  // warn on the page and on each rule it can't fire for.
  const { liveTrafficGroups } = useTraffic() || {};
  const usbCapture = useWebUsbCapture();
  const androidMix = androidCaptureMix(liveTrafficGroups);
  const usbInPlay = androidMix.usb > 0 || usbCaptureActive(usbCapture);

  // Pull the known device list so rules can be scoped from a dropdown.
  useEffect(() => {
    fetch(`${PROXY_BASE}/proxy/devices`)
      .then(r => r.json())
      .then(d => {
        // Platform tabs ("iOS Mobile") first — they match every device of that platform —
        // then each device's own id or IP. The proxy matches a rule against all of them.
        const plats = (d.devices || []).map(x => x.platform).filter(p => p && p !== 'Unknown');
        const ids = (d.devices || []).map(x => x.deviceId).filter(Boolean);
        setDevices([...new Set([...plats, ...ids])]);
      })
      .catch(() => {});
  }, []);

  // ── Own WebSocket ───────────────────────────────────────────────────────
  useEffect(() => {
    const wsUrl = resolveProxyWsUrl();
    let ws, dead = false;
    function connect() {
      if (dead) return;
      ws = new WebSocket(withWsToken(wsUrl));
      wsRef.current = ws;
      ws.onopen = () => {
        setWsState('connected');
        ws.send(JSON.stringify({ type: 'get_iu_rules' }));
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'iu_rules' && msg.data) {
            setEnabled(!!msg.data.enabled);
            setRules(Array.isArray(msg.data.rules) ? msg.data.rules : []);
            const rej = msg.data.rejected;
            const pend = pendingRef.current;
            if (rej && pend && rej.clientRef === pend.ref) {
              setDraft(pend.draft);
              setEditingId(pend.editingId);
              setRejectedMsg('Rule not saved: ' + reasonText(rej.reason));
              pendingRef.current = null;
            }
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { setWsState('disconnected'); if (!dead) setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    }
    connect();
    return () => { dead = true; try { ws?.close(); } catch {} };
  }, []);

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleMaster = () => send({ type: 'toggle_iu_rewrite', enabled: !enabled });
  const toggleRule = (id, val) => send({ type: 'toggle_iu_rule', id, enabled: val });
  const deleteRule = (id) => { if (window.confirm('Delete this rewrite rule?')) send({ type: 'delete_iu_rule', id }); };

  const startEdit = (rule) => {
    const field = FIELD_META[rule.field] ? rule.field : 'iu';
    setEditingId(rule.id);
    setRejectedMsg('');
    setDraft({
      id: rule.id,
      label: rule.label || '',
      field,
      match: rule.match || '',
      replace: rule.replace || '',
      key: rule.key || '',
      value: rule.value || '',
      randomValue: rule.randomValue || '',
      iuPrefix: rule.iuPrefix || '',
      device: rule.device || '',
      targets: { ...(rule.targets || {}) },
      enabled: rule.enabled !== false,
    });
  };
  const cancelEdit = () => { setEditingId(null); setDraft(EMPTY_DRAFT); setRejectedMsg(''); };

  // When the field changes, reset targets to that field's allowed channels (all on).
  const changeField = (field) => {
    const channels = FIELD_META[field].channels;
    const targets = {};
    channels.forEach(c => { targets[c] = true; });
    setDraft(d => ({ ...d, field, targets }));
  };

  const saveDraft = () => {
    const meta = FIELD_META[draft.field];
    setRejectedMsg('');
    const isRandom = draft.field === 'paramAdd' && draft.randomValue === 'uuid';
    if (isRandom) {
      if (!/^[A-Za-z0-9_.-]{1,64}$/.test(draft.key.trim())) { window.alert('A query param name is required (letters, digits, _ . -).'); return; }
      const v = validateRandomPpidDraft(draft);
      if (!v.ok) { window.alert(v.errors.join('\n')); return; }
      const clientRef = 'c_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      pendingRef.current = { ref: clientRef, draft, editingId };
      send({ type: 'set_iu_rule', rule: { ...draft, iuPrefix: v.iuPrefix, value: '', clientRef } });
      cancelEdit();
      return;
    }
    if (meta.kvp) {
      if (!draft.key.trim()) { window.alert('A cust_params key is required.'); return; }
      if (meta.kvp === 'add' && !draft.value.trim()) { window.alert('A value is required for Add KVP.'); return; }
    } else if (meta.param === 'add') {
      if (!/^[A-Za-z0-9_.-]{1,64}$/.test(draft.key.trim())) { window.alert('A query param name is required (letters, digits, _ . -).'); return; }
      if (draft.key.trim() === 'cust_params') { window.alert('Use Add KVP to set a key inside cust_params.'); return; }
      if (!draft.value.trim()) { window.alert(`A value is required for ${meta.name}.`); return; }
    } else if (meta.param) {
      if (!/^[A-Za-z0-9_.-]{1,64}$/.test(draft.value.trim())) { window.alert('A query param name is required (letters, digits, _ . -).'); return; }
    } else if (meta.fixed) {
      // fixed pair, nothing to fill in
    } else {
      if (!draft.match.trim()) { window.alert('A match pattern is required.'); return; }
      if (!draft.replace.trim()) { window.alert('A replacement value is required.'); return; }
    }
    const multi = meta.channels.length > 1;
    if (multi && !meta.channels.some(c => draft.targets[c])) {
      window.alert(`Pick at least one target (${meta.channels.map(c => CHANNEL_LABEL[c]).join(' or ')}).`);
      return;
    }
    send({ type: 'set_iu_rule', rule: draft });
    cancelEdit();
  };

  const dot = (color) => (
    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6 }} />
  );

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: C.ink }}>🔀 IU Rewrite</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: C.muted, maxWidth: 640 }}>
            Rewrite live ad requests before they reach GAM, Prebid, or APS — swap the
            <b> ad unit</b>, map a <b>size</b>, change a <b>stored-request id</b>, or point an
            <b> APS slot</b> at a test UUID. Rules use wildcards (<code style={{ fontSize: 12 }}>*</code>),
            carried into the replacement.
          </p>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 10, whiteSpace: 'nowrap',
          color: wsState === 'connected' ? C.pass : C.warn,
          background: (wsState === 'connected' ? C.pass : C.warn) + '22',
        }}>
          {wsState === 'connected' ? '● proxy connected' : `● ${wsState}`}
        </span>
      </div>

      {/* Master switch */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        padding: '14px 16px', margin: '16px 0', borderRadius: 10,
        background: enabled ? C.active + '14' : C.card,
        border: `1px solid ${enabled ? C.active + '66' : C.border}`,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
            Rewriting is {enabled ? 'ON' : 'OFF'}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {enabled
              ? 'Enabled rules below are actively rewriting matching requests.'
              : 'No requests are being rewritten. Turn on to apply the rules below.'}
          </div>
        </div>
        <Toggle on={enabled} onChange={toggleMaster} label={enabled ? 'On' : 'Off'} big />
      </div>

      {usbInPlay && (
        <div role="note" style={{
          padding: '12px 16px', margin: '0 0 16px', borderRadius: 10,
          background: C.warn + '18', border: `1px solid ${C.warn}66`, fontSize: 12, color: C.ink, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            ⚠ Rewrite can't change USB-captured (adb logcat) Android traffic
          </div>
          {androidMix.proxied > 0
            ? <>This session mixes Android traffic captured over USB ({androidMix.usb} requests) and through the proxy ({androidMix.proxied}). Rules only rewrite the proxied part. </>
            : <>All Android traffic in this session ({androidMix.usb} requests{usbCaptureActive(usbCapture) ? ', capture running now' : ''}) comes from USB capture, so Android rules will never fire. </>}
          USB capture only reads the phone's log after the app has already sent each request straight to Google; nothing passes
          through the proxy to rewrite. To rewrite Android ad units, capture through the Wi-Fi proxy instead, with a QA/debug
          build, or an emulator with the proxy certificate installed as a system certificate. iOS and other proxied devices are unaffected.
        </div>
      )}

      {rejectedMsg && (
        <div role="alert" data-testid="random-ppid-rejected" style={{ fontSize: 12, padding: '8px 10px', margin: '0 0 12px', borderRadius: 6, background: 'var(--error-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
          {rejectedMsg}
        </div>
      )}

      {/* Rule editor */}
      <div style={{ padding: 16, borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 12 }}>
          {editingId ? 'Edit rule' : 'Add a rule'}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Rewrite" hint="What this rule changes">
            <select value={draft.field} onChange={e => changeField(e.target.value)} style={{ ...inputStyle, minWidth: 190 }}>
              {Object.entries(FIELD_META).map(([k, m]) => <option key={k} value={k}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Label (optional)" hint="A name to recognise this rule" grow>
            <input
              value={draft.label}
              onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
              placeholder="e.g. All HB banners → test"
              style={inputStyle}
            />
          </Field>
        </div>

        {FIELD_META[draft.field].fixed ? (
          <div role="note" style={{ fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--info-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
            {FIELD_META[draft.field].note}
          </div>
        ) : FIELD_META[draft.field].kvp ? (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Key" hint={FIELD_META[draft.field].keyHint} grow>
              <input
                value={draft.key}
                onChange={e => setDraft(d => ({ ...d, key: e.target.value }))}
                placeholder={FIELD_META[draft.field].keyPh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            {FIELD_META[draft.field].kvp === 'add' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: 22, color: C.muted, fontSize: 18 }}>=</div>
                <Field label="Value" hint={FIELD_META[draft.field].valueHint} grow>
                  <input
                    value={draft.value}
                    onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                    placeholder={FIELD_META[draft.field].valuePh}
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                  />
                </Field>
              </>
            )}
          </div>
        ) : FIELD_META[draft.field].param === 'add' ? (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label={FIELD_META[draft.field].keyLabel || 'Query param'} hint={FIELD_META[draft.field].keyHint} grow>
              <input
                value={draft.key}
                onChange={e => setDraft(d => ({ ...d, key: e.target.value, randomValue: e.target.value.trim() === 'ppid' ? d.randomValue : '' }))}
                placeholder={FIELD_META[draft.field].keyPh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 22, color: C.muted, fontSize: 18 }}>=</div>
            <Field label="Value" hint={FIELD_META[draft.field].valueHint} grow>
              <input
                value={draft.value}
                disabled={!!draft.randomValue}
                onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                placeholder={FIELD_META[draft.field].valuePh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            {draft.field === 'paramAdd' && draft.key.trim() === 'ppid' && (
              <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    data-testid="random-ppid-toggle"
                    checked={draft.randomValue === 'uuid'}
                    onChange={e => setDraft(d => (e.target.checked
                      ? { ...d, randomValue: 'uuid', value: '', iuPrefix: d.iuPrefix || RANDOM_PREFIX_DEFAULT }
                      : { ...d, randomValue: '' }))}
                  />
                  New random UUID on every request
                </label>
                {draft.randomValue === 'uuid' && (
                  <>
                    <Field label="Ad unit prefix" hint="Only requests whose final ad unit is this prefix or under it get a random ppid. Needs a leading / and two or more segments.">
                      <input
                        data-testid="random-ppid-prefix"
                        value={draft.iuPrefix}
                        onChange={e => setDraft(d => ({ ...d, iuPrefix: e.target.value }))}
                        placeholder={RANDOM_PREFIX_DEFAULT}
                        style={{ ...inputStyle, fontFamily: 'monospace' }}
                      />
                    </Field>
                    <div role="note" data-testid="random-ppid-note" style={{ fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>Test ad units only — switch this off after testing.</div>
                    <div data-testid="random-ppid-extra" style={{ fontSize: 11, color: 'var(--muted)' }}>
                      Each request looks like a new user to Google, so frequency caps and audience targeting will not match; a no-fill after enabling this is not automatically a mediation result. A random rule that never fires shows no before to after in Requests; check there for ppid rows before reading any result.
                    </div>
                  </>
                )}
              </div>
            )}
            {!draft.randomValue && ppidLengthWarning(draft.field, draft.key, draft.value) && (
              <div role="alert" data-testid="ppid-warning" style={{ flexBasis: '100%', fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
                {ppidLengthWarning(draft.field, draft.key, draft.value)}
              </div>
            )}
          </div>
        ) : FIELD_META[draft.field].param ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Field label="Query param to remove" hint={FIELD_META[draft.field].valueHint} grow>
              <input
                value={draft.value}
                onChange={e => setDraft(d => ({ ...d, value: e.target.value, label: d.label || (e.target.value ? `Remove ${e.target.value}` : d.label) }))}
                placeholder={FIELD_META[draft.field].valuePh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            {draft.value.trim().toLowerCase() === 'adtest' && (
              <div role="note" style={{ fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
                ⚠ Removing <code>adtest</code> turns this device's test auctions into <b>live, billable</b> ones: real bids, real impressions and spend, sent through this proxy's datacenter IP (SSPs may flag it as invalid traffic). Use it briefly, on one device, and switch the rule off when done.
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Match" hint={FIELD_META[draft.field].matchHint} grow>
              <input
                value={draft.match}
                onChange={e => setDraft(d => ({ ...d, match: e.target.value }))}
                placeholder={FIELD_META[draft.field].matchPh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 22, color: C.muted, fontSize: 18 }}>→</div>
            <Field label="Replace with" hint={FIELD_META[draft.field].replaceHint} grow>
              <input
                value={draft.replace}
                onChange={e => setDraft(d => ({ ...d, replace: e.target.value }))}
                placeholder={FIELD_META[draft.field].replacePh}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </Field>
            {draft.field === 'bundleSwap' && (
              <div role="note" style={{ flexBasis: '100%', fontSize: 12, padding: '8px 10px', borderRadius: 6, background: 'var(--warn-bg)', color: 'var(--ink)', border: '1px solid var(--line)' }}>
                ⚠ Only the plain-text params change (GAM query params, the Prebid OpenRTB body); USB/logcat capture only reads requests, so nothing is rewritten there. The signed signals (<code>ms</code>, <code>a3p</code> bid tokens) still carry the real package, so Google and buyers can see the mismatch, and Liftoff/Meta still see the debug app. Without <code>adtest=on</code> these are <b>live, billable</b> auctions under the production app's name from this proxy's datacenter IP. Pair it with adtest, scope it to one device, and switch it off when done.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Apply to:</span>
            {FIELD_META[draft.field].fixed
              ? <span style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>
                  {FIELD_META[draft.field].channels.map(ch => CHANNEL_LABEL[ch]).join(' + ')} <span style={{ color: C.muted, fontWeight: 400 }}>(always both)</span>
                </span>
              : FIELD_META[draft.field].channels.length > 1
              ? FIELD_META[draft.field].channels.map(ch => (
                  <Check key={ch} label={CHANNEL_LABEL[ch]} checked={!!draft.targets[ch]}
                    onChange={v => setDraft(d => ({ ...d, targets: { ...d.targets, [ch]: v } }))} />
                ))
              : <span style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>
                  {CHANNEL_LABEL[FIELD_META[draft.field].channels[0]]} <span style={{ color: C.muted, fontWeight: 400 }}>(only)</span>
                </span>}
          </div>

          <Field
            label={draft.randomValue ? 'Device scope (required)' : 'Device scope'}
            hint={draft.randomValue ? 'Required for a random ppid rule' : 'Leave blank to apply to all devices'}
          >
            <input
              list="iu-devices"
              value={draft.device}
              onChange={e => setDraft(d => ({ ...d, device: e.target.value }))}
              placeholder="All devices"
              style={{ ...inputStyle, minWidth: 170 }}
            />
            <datalist id="iu-devices">
              {devices.map(d => <option key={d} value={d} />)}
            </datalist>
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={saveDraft} style={primaryBtn}>{editingId ? 'Save changes' : 'Add rule'}</button>
          {editingId && <button onClick={cancelEdit} style={ghostBtn}>Cancel</button>}
        </div>
      </div>

      {/* Rules list */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 8 }}>
        Rules ({rules.length})
      </div>
      {rules.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          No rules yet. Add one above to redirect an ad unit to your test IU.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rules.map(r => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
              borderRadius: 10, background: C.card,
              border: `1px solid ${r.enabled && enabled ? C.active + '55' : C.border}`,
              opacity: r.enabled ? 1 : 0.6,
            }}>
              <Toggle on={r.enabled} onChange={() => toggleRule(r.id, !r.enabled)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: C.active + '22', color: C.active, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {(FIELD_META[r.field] || FIELD_META.iu).name}
                  </span>
                  {r.label || <span style={{ color: C.muted, fontWeight: 400 }}>(unnamed)</span>}
                </div>
                <div style={{ fontSize: 12, fontFamily: 'monospace', color: C.ink, wordBreak: 'break-all' }}>
                  {r.field === 'kvpAdd' && <>set <b>{r.key}</b> <span style={{ color: C.active }}>=</span> {r.value}</>}
                  {r.field === 'kvpRemove' && <>remove <b>{r.key}</b></>}
                  {r.field === 'metaField' && <>set Meta field <b>{r.key}</b> <span style={{ color: C.active }}>=</span> {r.value}</>}
                  {r.field === 'metaTestMode' && <><b>adtest=on</b> on GAM <span style={{ color: C.active }}>+</span> <b>TEST_MODE=1</b> on Meta</>}
                  {r.field === 'paramAdd' && describeRule(r) && <>{describeRule(r)}</>}
                  {r.field === 'paramAdd' && !describeRule(r) && <>set query param <b>{r.key}</b> <span style={{ color: C.active }}>=</span> {r.value}</>}
                  {r.field === 'paramRemove' && (
                    <>remove query param <b>{r.value}</b>{r.value === 'adtest' && <span style={{ color: C.muted }}> · ⚠ live, billable auctions</span>}</>
                  )}
                  {!FIELD_META[r.field]?.kvp && !FIELD_META[r.field]?.param && !FIELD_META[r.field]?.fixed && <>{r.match} <span style={{ color: C.active }}>→</span> {r.replace}</>}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  {Object.keys(r.targets || {}).filter(ch => r.targets[ch]).map(ch => (
                    <span key={ch}>{dot(CHANNEL_DOT[ch] || C.active)}{CHANNEL_LABEL[ch] || ch}&nbsp;&nbsp;</span>
                  ))}
                  {r.device && (
                    <span style={{ padding: '1px 7px', borderRadius: 8, background: 'var(--surface)', border: `1px solid ${C.border}` }}>
                      📱 {r.device}
                    </span>
                  )}
                </div>
                {r.warning && (
                  <div data-testid="rule-warning" style={{ fontSize: 11, color: 'var(--ink)', marginTop: 4, padding: '4px 8px', borderRadius: 6, background: 'var(--warn-bg)' }}>{r.warning}</div>
                )}
                {!r.randomValue && gamBodyHint(r) && (
                  <div data-testid="rule-gam-hint" style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{gamBodyHint(r)}</div>
                )}
              </div>
              {(() => {
                const blocked = ruleUsbBlocked(r, liveTrafficGroups);
                if (!blocked || !r.enabled) return null;
                return (
                  <span
                    title={blocked === 'all'
                      ? 'Every request this rule could match was captured over USB (adb logcat), which the proxy never sees, so it cannot rewrite them. Capture through the Wi-Fi proxy to use this rule.'
                      : 'Only the proxied part of this traffic can be rewritten; USB-captured (adb logcat) requests pass through unchanged.'}
                    style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, whiteSpace: 'nowrap', background: C.warn + '22', color: C.warn }}
                  >
                    {blocked === 'all' ? "Won't fire: USB capture" : 'Partial: USB capture'}
                  </span>
                );
              })()}
              <button onClick={() => startEdit(r)} style={miniBtn}>Edit</button>
              <button onClick={() => deleteRule(r.id)} style={{ ...miniBtn, color: C.fail }}>Delete</button>
            </div>
          ))}
        </div>
      )}

      <p style={{ marginTop: 24, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
        Rewritten requests are tagged in the dashboard so you can confirm the swap landed.
        Rules persist on the proxy and survive restarts. Prebid bodies are re-sent with
        chunked encoding after the swap, so the auction sees the test unit with no length mismatch.
      </p>
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────────────────────
function Field({ label, hint, grow, children }) {
  return (
    <div style={{ marginBottom: 12, flex: grow ? 1 : undefined, minWidth: grow ? 180 : undefined }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Toggle({ on, onChange, big }) {
  const w = big ? 46 : 38, h = big ? 26 : 22, knob = h - 6;
  return (
    <button
      onClick={onChange}
      aria-pressed={on}
      style={{
        width: w, height: h, borderRadius: h, border: 'none', cursor: 'pointer', flexShrink: 0,
        background: on ? '#22c55e' : '#9ca3af', position: 'relative', transition: 'background .15s', padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? w - knob - 3 : 3,
        width: knob, height: knob, borderRadius: '50%', background: 'var(--card)',
        transition: 'left .15s',
      }} />
    </button>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13,
  borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--ink)',
};
const primaryBtn = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
  background: '#3b82f6', color: '#fff', cursor: 'pointer',
};
const ghostBtn = {
  padding: '8px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8,
  border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer',
};
const miniBtn = {
  padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6,
  border: '1px solid var(--border)', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', flexShrink: 0,
};
